// Turns pasted workout text (an Instagram reel description, a note, text copied from a web page; English or
// German) into a workout draft: sections, their format, and each exercise line matched to the library.
// Pure and offline: no AI, no network. Lines the matcher is not sure about come back with candidates, so the
// person decides ("did you mean") or creates a new exercise. The pasted text itself is never part of a saved
// workout: the caller keeps only the structured result (our own summary) plus an optional source label.
import { matchExercise } from '../match';
import { EXERCISES } from '../seed';
import type { Amount, Exercise, SectionFormat, SectionKind } from '../types';

/** How sure the import is about an exercise line. */
export type ImportMatchStatus =
  /** The matcher is confident (exact name or alias, or a clear winner). */
  | 'sure'
  /** A likely match is preselected, but the person should check it. */
  | 'check'
  /** Nothing close enough: pick a candidate, create a new exercise, or leave it out. */
  | 'new';

export interface ImportCandidate {
  exerciseId: string;
  name: string;
  /** 0 to 1, from matchExercise. */
  score: number;
}

export interface ImportExercise {
  /** The exercise name as written, with amounts, emoji and list marks removed. Prefills "create new". */
  name: string;
  amount?: Amount;
  /** Sets from "5x5" or "3 sets of 10", when the line says so. */
  sets?: number;
  /** Short facts we keep in our own words: a weight ("16 kg"), a distance ("200 m"), sets in a circuit. */
  note?: string;
  status: ImportMatchStatus;
  /** The preselected exercise ('sure' and 'check'). */
  exerciseId?: string;
  /** Best first, at most 5. */
  candidates: ImportCandidate[];
  /** Who picked exerciseId: the offline matcher, or the AI (name matching help). */
  by: 'matcher' | 'ai';
  /** An AI draft marked this line's amount or name as a guess: the person should check it. */
  unsure?: boolean;
}

export interface ImportSection {
  kind: SectionKind;
  format: SectionFormat;
  /** What the parser had to assume, in plain words ("40 s per exercise assumed"). */
  assumed: string[];
  exercises: ImportExercise[];
}

export interface ImportDraft {
  /** A name suggestion from the first line that reads like a title. */
  title?: string;
  /** The first @handle in the text, as a suggestion for the source label. */
  sourceHint?: string;
  sections: ImportSection[];
  /** Lines that were neither a heading, a format nor an exercise (hashtags, calls to follow, prose). */
  ignoredLines: number;
  /** Those lines, cleaned and shortened, so the preview can name them. In memory only, never saved. */
  ignored: string[];
  /** An AI draft's own notes on what it guessed ("rest time not given, 60 s assumed"). In memory only. */
  notes?: string[];
}

export interface ParseOptions {
  /** The library to match against; default the built-in catalog. */
  exercises?: Exercise[];
}

/**
 * The AI name matching help (apps/ai, feature match_names): gets the names the matcher was not sure about and
 * may return a library exercise id for each, or null to leave the decision with the person.
 */
export type ImportAiFallback = (unmatched: string[]) => Promise<Array<{ name: string; exerciseId: string | null }>>;

/** From this score up, a non-confident match is preselected ('check'); below it the line is 'new'. */
export const IMPORT_CHECK_SCORE = 0.6;
/** Longer pastes are cut, so a whole web page cannot freeze the preview. */
export const IMPORT_MAX_CHARS = 20_000;

// Limits shared with the workout builder (apps/web/src/lib/builder.ts), so an imported draft opens valid.
export const IMPORT_LIMITS = { rounds: [1, 50], workSec: [5, 3600], restSec: [0, 600], roundRestSec: [0, 900], minutes: [1, 180], sets: [1, 20], reps: [1, 500], seconds: [5, 3600] } as const;
const LIMIT = IMPORT_LIMITS;
const clamp = (n: number, [min, max]: readonly [number, number]) => Math.min(max, Math.max(min, Math.round(n)));

// Characters --------------------------------------------------------------------------------------

const DASH = '\\-\\u2010-\\u2015\\u2212'; // hyphen, en dash, em dash, minus
const KEYCAP = /([0-9])\uFE0F?\u20E3/gu;
const KEYCAP_TEN = /\u{1F51F}/gu;
const PICTO = /\p{Extended_Pictographic}|[\u{1F1E6}-\u{1F1FF}]|[\u{1F3FB}-\u{1F3FF}]|\uFE0F|\uFE0E|\u200D|\u20E3/gu;
const HASHTAG = /(^|\s)#[\p{L}\p{N}_]+/gu;
const MENTION = /(^|[\s(])@([A-Za-z0-9._]{2,30})/g;
const URL = /\b(?:https?:\/\/|www\.)\S+/gi;

/** Removes emoji, hashtags, links and odd spacing; keycap digits (the emoji "1") become "1.". */
function cleanLine(line: string): string {
  return line
    .replace(KEYCAP, '$1. ').replace(KEYCAP_TEN, '10. ')
    .replace(URL, ' ')
    .replace(HASHTAG, ' ')
    .replace(PICTO, ' ')
    .replace(/[\u00A0\u2000-\u200B\u202F\u3000\t]/g, ' ')
    .replace(/\u00D7/g, 'x')
    .replace(/[\u2018\u2019\u2032]/g, "'").replace(/[\u201C\u201D\u2033]/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

/** List marks: bullets, "1." "1)" "(1)", supersets "A1.", letters "a)", EMOM minute labels "Min 1:", "Odd:". */
const LIST_MARK = new RegExp(
  '^(?:' + [
    `[${DASH}*\u2022\u00B7\u25AA-\u25AB\u25B8\u25BA\u25CF\u25E6\u2192\u21D2>+~|]+`,
    '\\(?\\d{1,2}[.)](?!\\d)',
    '\\(\\d{1,2}\\)',
    '[A-Ea-e][1-9][.):]?(?=\\s)',
    '[A-Ea-e][.)](?=\\s)',
    '(?:min(?:ute)?n?|minute)\\s*\\d{1,2}(?:\\s*[' + DASH + ']\\s*\\d{1,2})?\\s*[:.)' + DASH + ']',
    '\\d{1,2}(?:st|nd|rd|th)?\\.?\\s*min(?:ute)?n?\\s*[:.)' + DASH + ']',
    '(?:odd|even|ungerade|gerade)(?:\\s+min(?:ute)?s?n?)?\\s*[:' + DASH + ']',
  ].join('|') + ')\\s*',
  'iu',
);

const SUPERSET_MARK = /^([A-Ea-e])[1-9][.):]?\s/u;

/** The text without list marks; group is the superset letter of "A1." or "B2)" (upper case). */
function stripListMark(line: string): { text: string; marked: boolean; group?: string } {
  let text = line;
  let marked = false;
  const group = SUPERSET_MARK.exec(line)?.[1]?.toUpperCase();
  // Twice: "1. - Swings", "Min 1: A) ..."
  for (let i = 0; i < 2; i++) {
    const m = LIST_MARK.exec(text);
    if (!m || !m[0]) break;
    text = text.slice(m[0].length);
    marked = true;
  }
  return { text: text.trim(), marked, ...(group ? { group } : {}) };
}

// Numbers and units ----------------------------------------------------------------------------------

const N = '(\\d{1,4}(?:[.,]\\d)?)';
const SEC = `(?:s|sec|secs|second|seconds|sek|sekunde|sekunden|''|"|\u2033)(?![\\p{L}])`;
const MIN = `(?:min|mins|minute|minutes|minuten|'(?!'))(?![\\p{L}])`;
const toNum = (s: string) => Number(s.replace(',', '.'));
const B = '(?<![\\p{L}\\d])'; // word start that works with umlauts

function re(source: string, flags = 'giu'): RegExp {
  return new RegExp(source, flags);
}

// Format hints ---------------------------------------------------------------------------------------

interface Hints {
  kind?: 'emom' | 'amrap' | 'continuous' | 'tabata';
  minutes?: number;
  rounds?: number;
  workSec?: number;
  restSec?: number;
  roundRestSec?: number;
  sets?: number;
  setRestSec?: number;
  /** "repeat 3x", "3 Mal wiederholen": belongs to the exercises above it. */
  repeat?: boolean;
  /** Supersets: the shared letter ("A" for A1/A2) or 'heading' ("Superset 1:"); the exercises alternate. */
  superset?: string;
}

interface Found { hints: Hints; rest: string }

/** Finds format phrases in a line, returns them and the line without them. */
function findFormat(line: string): Found {
  const hints: Hints = {};
  let rest = ` ${line} `;
  const take = (pattern: RegExp, apply: (m: RegExpExecArray) => void) => {
    rest = rest.replace(pattern, (...args) => {
      const m = args.slice(0, -2) as unknown as RegExpExecArray;
      apply(m);
      return ' ';
    });
  };


  // EMOM and friends ("EMOM 12", "12 min EMOM", "every minute on the minute for 10 minutes", "jede Minute")
  take(re(`${B}e[23]?mom\\b\\s*(?:x\\s*)?(?:${N}\\s*(?:${MIN})?)?`), (m) => { hints.kind = 'emom'; if (m[1]) hints.minutes = toNum(m[1]); });
  take(re(`${B}${N}\\s*(?:${MIN})?\\s*e[23]?mom\\b`), (m) => { hints.kind = 'emom'; hints.minutes = toNum(m[1]!); });
  take(re(`${B}(?:every minute on the minute|jede minute(?: auf die minute)?)\\b(?:\\s*(?:for|f(?:\u00FC|ue)r))?(?:\\s*${N}\\s*${MIN})?`), (m) => { hints.kind = 'emom'; if (m[1]) hints.minutes = toNum(m[1]); });
  // AMRAP ("AMRAP 15", "15 min AMRAP", "as many rounds as possible in 15 minutes", "so viele Runden wie moeglich")
  take(re(`${B}amrap\\b\\s*(?:${N}\\s*(?:${MIN})?)?`), (m) => { hints.kind = 'amrap'; if (m[1]) hints.minutes = toNum(m[1]); });
  take(re(`${B}${N}\\s*(?:${MIN})?\\s*amrap\\b`), (m) => { hints.kind = 'amrap'; hints.minutes = toNum(m[1]!); });
  take(re(`${B}(?:as many rounds as possible|so viele runden wie m(?:\u00F6|oe)glich)(?:\\s*(?:in|f(?:\u00FC|ue)r))?(?:\\s*${N}\\s*${MIN})?`), (m) => { hints.kind = 'amrap'; if (m[1]) hints.minutes = toNum(m[1]); });
  // "in 15 minutes" said apart from the AMRAP or EMOM words
  if ((hints.kind === 'amrap' || hints.kind === 'emom') && !hints.minutes) take(re(`${B}(?:(?:in|for|f(?:\u00FC|ue)r)\\s*)?${N}\\s*${MIN}`), (m) => { hints.minutes = toNum(m[1]!); });
  // Non-stop ("20 min non-stop", "20 Minuten ohne Pause", "nonstop for 20 min")
  take(re(`${B}${N}\\s*${MIN}\\s*(?:of\\s+)?(?:non[${DASH}\\s]?stop|continuous(?:ly)?|ohne pause|durchgehend|am st(?:\u00FC|ue)ck)\\b`), (m) => { hints.kind = 'continuous'; hints.minutes = toNum(m[1]!); });
  take(re(`${B}(?:non[${DASH}\\s]?stop|continuous(?:ly)?|ohne pause|durchgehend)\\b(?:\\s*(?:for|f(?:\u00FC|ue)r))?\\s*${N}\\s*${MIN}`), (m) => { hints.kind = 'continuous'; hints.minutes = toNum(m[1]!); });
  // Tabata
  take(re(`${B}tabata\\b`), () => { hints.kind = 'tabata'; });
  // Intervals: "40/20", "40s on / 20s off", "45 Sek Belastung, 15 Sek Pause", "work 40 rest 20"
  take(re(`${B}${N}\\s*(?:${SEC})?\\s*(?:on|work|arbeit|belastung)?\\s*[/|]\\s*${N}\\s*(?:${SEC})?\\s*(?:off|rest|pause)?(?![\\d.,]*\\s*(?:kg|kgs|lb|lbs|m\\b|km|reps?|wdh|x\\b))`), (m) => { hints.workSec = toNum(m[1]!); hints.restSec = toNum(m[2]!); });
  take(re(`${B}${N}\\s*(?:${SEC})?\\s*(?:on|work|arbeit|belastung)\\b[\\s,;/|${DASH}]*${N}\\s*(?:${SEC})?\\s*(?:off|rest|pause|erholung)\\b`), (m) => { hints.workSec = toNum(m[1]!); hints.restSec = toNum(m[2]!); });
  take(re(`${B}(?:work|arbeit|belastung)\\s*:?\\s*${N}\\s*(?:${SEC})?[\\s,;/|${DASH}]*(?:rest|pause|erholung)\\s*:?\\s*${N}\\s*(?:${SEC})?`), (m) => { hints.workSec = toNum(m[1]!); hints.restSec = toNum(m[2]!); });
  // Rounds ("3 rounds", "3 Runden", "4 Durchgaenge", "repeat 3x", "3 Mal wiederholen", "3x durch")
  take(re(`${B}(?:repeat|wiederhole(?:n)?)\\s*(?:it|this|that|das|alles|the circuit|den zirkel)?\\s*(?:x\\s*)?(\\d{1,2})\\s*(?:x|times|mal)?(?![\\p{L}])`), (m) => { hints.rounds = toNum(m[1]!); hints.repeat = true; });
  take(re(`${B}(\\d{1,2})\\s*(?:x|times|mal)\\s*(?:through|durch|wiederholen|repeat)\\b`), (m) => { hints.rounds = toNum(m[1]!); hints.repeat = true; });
  take(re(`${B}(\\d{1,2})\\s*(?:x\\s*)?(?:rounds?|rds|rnds|runden?|durchg(?:\u00E4|ae)nge?|durchl(?:\u00E4|ae)ufe?)(?:\\s+(?:total|insgesamt|of|von|\u00E0|a))?(?![\\p{L}])`), (m) => { hints.rounds = toNum(m[1]!); });
  take(re(`${B}(?:rounds?|runden)\\s*:?\\s*(\\d{1,2})(?![\\d\\p{L}])`), (m) => { hints.rounds = toNum(m[1]!); });
  // A sets line on its own ("4 sets", "3 Saetze")
  take(re(`^\\s*(\\d{1,2})\\s*(?:sets?|s(?:\u00E4|ae)tze?)(?:\\s+(?:each|je|of|\u00E0|a))?\\s*:?\\s*$`), (m) => { hints.sets = toNum(m[1]!); });
  return { hints, rest };
}

/** "3 rounds" and the like, left out of a title ("Full body, 3 rounds" is titled "Full body"). */
const ROUNDS_PHRASE = re(`[,;${DASH}]?\\s*${B}\\d{1,2}\\s*(?:x\\s*)?(?:rounds?|rds|runden?|durchg(?:\u00E4|ae)nge?)(?![\\p{L}])`);

/** Rest lines: "Rest 60 sec", "60s rest between rounds", "Pause 1 Min", "1:00 Pause". */
function findRest(line: string): { seconds: number; target?: 'round' | 'set' | 'exercise' } | null {
  const low = line.toLowerCase();
  if (!re(`${B}(?:rest(?:ing)?|pause|break|erholung|ruhe|recover|recovery)\\b`, 'iu').test(low)) return null;
  let seconds: number | null = null;
  const mmss = /(\d{1,2}):(\d{2})/.exec(low);
  const min = re(`${B}${N}\\s*${MIN}`, 'iu').exec(low);
  const sec = re(`${B}${N}\\s*${SEC}`, 'iu').exec(low);
  if (mmss) seconds = Number(mmss[1]) * 60 + Number(mmss[2]);
  else if (min || sec) seconds = (min ? toNum(min[1]!) * 60 : 0) + (sec ? toNum(sec[1]!) : 0);
  if (seconds === null) return null;
  const target = re('between rounds|after (?:each|every) round|zwischen (?:den )?runden|nach jede[rm]? runde', 'iu').test(low) ? 'round'
    : re('between sets|zwischen (?:den )?s(?:\u00E4|ae)tzen|nach jedem satz', 'iu').test(low) ? 'set'
      : re('between (?:the )?exercises|zwischen (?:den )?(?:\u00FC|ue)bungen|nach jeder (?:\u00FC|ue)bung', 'iu').test(low) ? 'exercise'
        : undefined;
  return { seconds, ...(target ? { target } : {}) };
}

/** True when what is left of a line after taking out formats or a rest phrase has no real words. */
function isEmptyRest(text: string): boolean {
  const words = text
    .toLowerCase()
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .split(/\s+/)
    .filter((w) => w && !REST_FILLER.has(w));
  return words.length === 0;
}

const REST_FILLER = new Set([
  'of', 'for', 'the', 'a', 'in', 'x', 'and', 'then', 'with', 'each', 'every', 'between', 'after', 'round', 'rounds', 'set', 'sets',
  'exercise', 'exercises', 'workout', 'total', 'min', 'mins', 'minute', 'minutes', 's', 'sec', 'secs', 'seconds', 'do', 'complete',
  'perform', 'as', 'needed', 'circuit', 'format', 'style', 'only', 'time', 'cap', 'on', 'off', 'work', 'rest', 'break', 'recovery',
  'f\u00FCr', 'fuer', 'der', 'die', 'das', 'den', 'dem', 'von', 'mit', 'und', 'dann', 'je', 'jede', 'jeder', 'jedem', 'zwischen', 'nach',
  'runde', 'runden', 'satz', 's\u00E4tzen', 'saetzen', 'training', 'resting', 's\u00E4tze', 'saetze', '\u00FCbung', 'uebung', '\u00FCbungen', 'uebungen', 'minuten', 'sek', 'sekunden', 'pause',
  'erholung', 'ruhe', 'insgesamt', 'zirkel', 'als', 'im', 'in', 'pro', 'mal', 'it', 'this', 'that', 'repeat', 'times', 'durch', 'alles',
  'bei', 'bis', 'zu', 'zum', 'zur', 'an', 'am', 'ab', 'auf', 'so', 'wie', 'lange', 'long', 'you', 'can', 'du', 'kannst',
]);

// Section headings -----------------------------------------------------------------------------------

const HEADINGS: Array<[RegExp, SectionKind]> = [
  [re(`^(?:warm[${DASH}\\s]?up|warmup|aufw(?:\u00E4|ae)rm(?:en|ung|programm)|einw(?:\u00E4|ae)rm(?:en|ung)|mobili[sz]ation|mobility|activation|aktivierung|vorbereitung|prep)(?![\\p{L}])`, 'iu'), 'warmup'],
  [re(`^(?:cool[${DASH}\\s]?down|cooldown|abk(?:\u00FC|ue)hl(?:en|ung)|auslaufen|stretch(?:ing)?|dehn(?:en|ung|\u00FCbungen|uebungen)|recovery|entspannung)(?![\\p{L}])`, 'iu'), 'cooldown'],
  [re(`^(?:main(?:\\s+(?:set|part|workout|block|circuit))?|the workout|workout|haupt(?:teil|block|workout|programm)|training|circuit|zirkel|finisher|metcon|wod|strength|kraft(?:teil|block)?|conditioning|(?:part|teil|block|round|runde|superset|tri[${DASH}\\s]?set)\\s*(?:[a-e1-9]|one|two|three|eins|zwei|drei)?)(?![\\p{L}])`, 'iu'), 'main'],
];

interface Heading { kind: SectionKind; after: string }

function findHeading(line: string): Heading | null {
  for (const [pattern, kind] of HEADINGS) {
    const m = pattern.exec(line);
    if (!m) continue;
    const after = line.slice(m[0].length);
    // "Warm-up: 5 min" or "Warm-up (2 rounds)" or "Cool-down: Child's pose 60s" or just "Warm-up"
    const sep = re(`^\\s*(?:[:.${DASH}|]|\\(|$)`, 'u').exec(after);
    if (sep || isEmptyRest(after)) return { kind, after: after.replace(re(`^\\s*[:.${DASH}|]?\\s*`, 'u'), '').replace(/^\(|\)$/g, '').trim() };
  }
  return null;
}

// Noise ----------------------------------------------------------------------------------------------

const NOISE = re(
  `${B}(?:follow|folg(?:e|t)|save (?:this|it|for)|speicher|share|teile? (?:das|es|mit)|comment|kommentier|tag (?:a|your|someone)|markier|link in (?:bio|my bio)|in bio|dm me|subscribe|abonnier|credit|music|song|audio|giveaway|discount|rabatt|code\\b|let me know|lass mich|try this|probier|who'?s in|wer ist dabei|send this|schick|double tap|turn on|notifications|check out|full video|ganzes video|my app|meine app|coaching|sign up|anmelden)`,
  'iu',
);

/** Words that make a line a workout title rather than an exercise ("20 MIN KETTLEBELL FULL BODY"). */
const TITLE_STRONG = re(`${B}(?:workout|training|routine|session|wod|einheit|ganzk(?:\u00F6|oe)rper|full[${DASH}\\s]?body|upper body|lower body|oberk(?:\u00F6|oe)rper|unterk(?:\u00F6|oe)rper|leg day|push day|pull day|hiit)(?![\\p{L}])`, 'iu');

const TITLE_WORDS = re(`${B}(?:workout|training|routine|session|wod|circuit|einheit|ganzk(?:\u00F6|oe)rper|full[${DASH}\\s]?body|upper body|lower body|leg day|push day|pull day|beine|bauch|abs|core|hiit|kettlebell|dumbbell|bodyweight)(?![\\p{L}])`, 'iu');

// Amounts on an exercise line --------------------------------------------------------------------------

interface Parsed { name: string; amount?: Amount; sets?: number; note?: string; hasAmount: boolean }

function parseExerciseLine(text: string): Parsed {
  let s = ` ${text} `;
  const notes: string[] = [];
  let sets: number | undefined;
  let reps: number | undefined;
  let repsMax: number | undefined;
  let seconds: number | undefined;
  let perSide = false;
  const take = (pattern: RegExp, apply: (m: string[]) => void) => {
    s = s.replace(pattern, (...args) => {
      apply(args.slice(0, -2) as string[]);
      return ' ';
    });
  };
  const range = `(\\d{1,3})(?:\\s*[${DASH}]\\s*(\\d{1,3}))?`;

  // Weights and distances become a note in our words.
  // Two bells: "2x24kg", "2 x 16 kg"
  take(re(`${B}([2-4])\\s*x\\s*${N}\\s*(kg|kgs|lb|lbs)(?![\\p{L}])`), (m) => { notes.push(`${m[1]} x ${m[2]} ${m[3]!.toLowerCase().startsWith('k') ? 'kg' : 'lb'}`); });
  take(re(`[@(]?\\s*${N}(?:\\s*/\\s*${N})?\\s*(kg|kgs|lb|lbs|pounds?)(?![\\p{L}])\\s*\\)?`), (m) => { notes.push(`${m[1]}${m[2] ? `/${m[2]}` : ''} ${m[3]!.toLowerCase().startsWith('k') ? 'kg' : 'lb'}`); });
  // Distances are never reps: "5 km run", "400 m row", "1 mile", "bear crawl 5 m"
  take(re(`${B}(\\d{1,5}(?:[.,]\\d{1,2})?)\\s*(km|kilometers?|kilometres?|kilometer|m|meters?|metres?|meter|miles?|mi)(?![\\p{L}])`), (m) => {
    const unit = m[2]!.toLowerCase();
    const label = unit.startsWith('k') ? 'km' : unit.startsWith('mi') ? (m[1] === '1' ? 'mile' : 'miles') : 'm';
    notes.push(`${m[1]!.replace(',', '.')} ${label}`);
  });
  // Per side
  take(re(`${B}(?:on\\s+)?(?:each|per|pro|je|jede[rns]?)\\s+(?:side|seite|leg|bein|arm|hand|direction|richtung)(?![\\p{L}])|/\\s*(?:side|seite|leg|bein|arm)(?![\\p{L}])|${B}(?:e/s|ea\\.?|each|beidseitig)(?![\\p{L}])|\\(\\s*(?:l\\s*/\\s*r|r\\s*/\\s*l|l\\s*\\+\\s*r)\\s*\\)|${B}l\\s*/\\s*r(?![\\p{L}])`), () => { perSide = true; });
  // Sets to the limit: "4 x max", "3 Saetze max"
  take(re(`${B}(\\d{1,2})\\s*(?:x|sets?|s(?:\u00E4|ae)tze?)\\s*(?:of\\s*)?(?:max(?:imal)?|amrap|failure|to failure|bis zum versagen)(?:\\s*reps?)?(?![\\p{L}])`), (m) => { sets = toNum(m[1]!); notes.push('max reps'); });
  // To the limit without a sets count: "Dips to failure", "Liegestuetze bis zum Versagen", "Burpees until done"
  take(re(`${B}(?:to failure|until failure|until done|max(?:imal)? reps|as many reps as possible|bis zum versagen|so viele wie m(?:\u00F6|oe)glich)(?![\\p{L}])`), () => { notes.push('max reps'); });
  // Sets x time: "3x30s", "3 x 1 min"
  take(re(`${B}(\\d{1,2})\\s*x\\s*${N}\\s*(${SEC}|${MIN})`), (m) => { sets = toNum(m[1]!); seconds = toNum(m[2]!) * (/^m|^'$/i.test(m[3]!.trim()) ? 60 : 1); });
  // Sets of reps: "3 sets of 10", "3 Saetze a 12", "4 sets x 8-10", "3 sets 30 sec"
  take(re(`${B}(\\d{1,2})\\s*(?:sets?|s(?:\u00E4|ae)tze?)\\s*(?:of|x|\u00E0|a|mit|von|zu|je|with)?\\s*${N}\\s*(${SEC}|${MIN})`), (m) => { sets = toNum(m[1]!); seconds = toNum(m[2]!) * (/^m|^'$/i.test(m[3]!.trim()) ? 60 : 1); });
  take(re(`${B}(\\d{1,2})\\s*(?:sets?|s(?:\u00E4|ae)tze?)\\s*(?:of|x|\u00E0|a|mit|von|zu|je|with)?\\s*${range}(?:\\s*(?:reps?|wdh\\.?|wiederholungen))?(?![\\d\\p{L}])`), (m) => { sets = toNum(m[1]!); reps = toNum(m[2]!); if (m[3]) repsMax = toNum(m[3]); });
  // "5x5", "4 x 8-10", "3x12"
  take(re(`${B}(\\d{1,2})\\s*[x*]\\s*${range}(?:\\s*(?:reps?|wdh\\.?|wiederholungen))?(?![\\d\\p{L}])`), (m) => { sets = toNum(m[1]!); reps = toNum(m[2]!); if (m[3]) repsMax = toNum(m[3]); });
  // A lone sets count: "4 sets", "3 Saetze"
  take(re(`${B}(\\d{1,2})\\s*(?:sets?|s(?:\u00E4|ae)tze?)(?![\\p{L}])`), (m) => { sets = toNum(m[1]!); });
  // Times: "1:30", "1 min 30 s", "45 sec", "1 Minute", "30''"
  take(re(`${B}(\\d{1,2}):(\\d{2})(?!\\d)`), (m) => { seconds = Number(m[1]) * 60 + Number(m[2]); });
  take(re(`${B}(\\d{1,3})\\s*[${DASH}]\\s*(\\d{1,3})\\s*(${SEC}|${MIN})`), (m) => {
    const unit = /^m|^'$/i.test(m[3]!.trim()) ? 60 : 1;
    seconds = toNum(m[1]!) * unit;
    notes.push(`up to ${unit === 60 ? `${m[2]} min` : `${m[2]} s`}`);
  });
  take(re(`${B}${N}\\s*${MIN}(?:\\s*${N}\\s*${SEC})?`), (m) => { seconds = toNum(m[1]!) * 60 + (m[2] ? toNum(m[2]) : 0); });
  take(re(`${B}${N}\\s*${SEC}`), (m) => { seconds = toNum(m[1]!); });
  // Reps: "12 reps", "10-12 Wdh.", "15x", "x12"
  if (reps === undefined) take(re(`${B}${range}\\s*(?:reps?|wdh\\.?|wh\\.?|wiederholungen|times|mal|x)(?![\\p{L}\\d])`), (m) => { reps = toNum(m[1]!); if (m[2]) repsMax = toNum(m[2]); });
  if (reps === undefined) take(re(`${B}x\\s*${range}(?![\\d\\p{L}])`), (m) => { reps = toNum(m[1]!); if (m[2]) repsMax = toNum(m[2]); });
  // A bare number in front ("12 Swings", "10-12 Push-ups") or at the end ("Swings: 15")
  if (reps === undefined && seconds === undefined) take(re(`^\\s*${range}\\s+(?=[\\p{L}])`, 'iu'), (m) => { reps = toNum(m[1]!); if (m[2]) repsMax = toNum(m[2]); });
  if (reps === undefined && seconds === undefined) take(re(`[\\s:,${DASH}]\\s*${range}\\s*$`, 'iu'), (m) => { reps = toNum(m[1]!); if (m[2]) repsMax = toNum(m[2]); });

  let amount: Amount | undefined;
  if (seconds !== undefined) amount = { kind: 'time', seconds: clamp(seconds, LIMIT.seconds), ...(perSide ? { perSide } : {}) };
  else if (reps !== undefined) {
    const r = clamp(reps, LIMIT.reps);
    const max = repsMax !== undefined && repsMax > r ? clamp(repsMax, LIMIT.reps) : undefined;
    amount = { kind: 'reps', reps: r, ...(max ? { repsMax: max } : {}), ...(perSide ? { perSide } : {}) };
  }
  const name = tidyName(s);
  return {
    name,
    ...(amount ? { amount } : {}),
    ...(sets !== undefined ? { sets: clamp(sets, LIMIT.sets) } : {}),
    ...(notes.length ? { note: notes.join(', ') } : {}),
    hasAmount: amount !== undefined || sets !== undefined || notes.length > 0 || perSide,
  };
}

/** Leftover punctuation and joining words off the ends; ALL CAPS to normal case. */
function tidyName(text: string): string {
  let name = text
    .replace(/[()[\]{}]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const edge = re(`^(?:[\\s:,;.!?|/@+=${DASH}]|of|\u00E0|a|x|je|mit|with|reps?|wdh\\.?)+(?=\\s|$)|(?:\\s(?:of|\u00E0|x|je|mit|with|reps?|wdh\\.?|und|and|&))+$|[\\s:,;.!?|/@+=${DASH}]+$|^[\\s:,;.!?|/@+=${DASH}]+`, 'iu');
  for (let i = 0; i < 4; i++) name = name.replace(edge, '').trim();
  const letters = name.replace(/[^\p{L}]/gu, '');
  if (letters.length > 3 && letters === letters.toUpperCase()) {
    name = name.toLowerCase().replace(/^\p{L}/u, (c) => c.toUpperCase());
  }
  return name.slice(0, 80);
}

// Pass 1: classify each line ---------------------------------------------------------------------------

type Item =
  | { type: 'blank' }
  | { type: 'noise'; text: string; title?: string }
  | { type: 'heading'; kind: SectionKind; hints: Hints; exercise?: ImportExercise }
  | { type: 'format'; hints: Hints; title?: string }
  | { type: 'rest'; seconds: number; target?: 'round' | 'set' | 'exercise' }
  | { type: 'exercise'; exercise: ImportExercise; hints: Hints; group?: string };

function hasHints(h: Hints): boolean {
  return Object.keys(h).length > 0;
}

/** The offline match of one name: status, the preselected exercise and up to 5 candidates. */
export function matchImportName(name: string, exercises: Exercise[]): Pick<ImportExercise, 'status' | 'exerciseId' | 'candidates'> {
  const result = matchExercise(name, exercises);
  const candidates = result.candidates.map((c) => ({ exerciseId: c.exercise.id, name: c.exercise.name, score: c.score }));
  const best = candidates[0];
  const status: ImportMatchStatus = result.confident ? 'sure' : best && best.score >= IMPORT_CHECK_SCORE ? 'check' : 'new';
  return { status, ...(status !== 'new' && best ? { exerciseId: best.exerciseId } : {}), candidates };
}

function matchLine(parsed: Parsed, exercises: Exercise[]): ImportExercise {
  const m = matchImportName(parsed.name, exercises);
  return {
    name: parsed.name,
    ...(parsed.amount ? { amount: parsed.amount } : {}),
    ...(parsed.sets !== undefined ? { sets: parsed.sets } : {}),
    ...(parsed.note ? { note: parsed.note } : {}),
    status: m.status,
    ...(m.exerciseId ? { exerciseId: m.exerciseId } : {}),
    candidates: m.candidates,
    by: 'matcher',
  };
}

/** An exercise from a line, or null when the line reads like prose, a call to action or a title. */
function exerciseFrom(text: string, marked: boolean, exercises: Exercise[]): ImportExercise | null {
  if (!text || NOISE.test(text)) return null;
  const parsed = parseExerciseLine(text);
  if (!parsed.name || !/\p{L}{2}/u.test(parsed.name)) return null;
  if (re(`^(?:rest(?:ing)?|pause|break|erholung|ruhe|recovery)(?![\\p{L}])`, 'iu').test(parsed.name)) return null;
  const words = parsed.name.split(/\s+/).length;
  if (words > 7) return null;
  if (/[?]$/.test(text) && !parsed.hasAmount) return null;
  const ex = matchLine(parsed, exercises);
  const best = ex.candidates[0]?.score ?? 0;
  if (ex.status !== 'sure' && !marked && TITLE_STRONG.test(parsed.name)) return null;
  if (parsed.hasAmount || marked) return ex;
  // No amount and no list mark: only a clear exercise name counts, short and not a sentence.
  if (words > 5 || /[!]$/.test(text)) return null;
  return ex.status === 'sure' || best >= 0.75 ? ex : null;
}

function classify(raw: string, exercises: Exercise[]): Item {
  const cleaned = cleanLine(raw);
  if (!cleaned) return raw.trim() ? { type: 'noise', text: raw.trim().slice(0, 120) } : { type: 'blank' };
  const { text, marked, group } = stripListMark(cleaned);
  if (!text) return { type: 'blank' };
  if (!/[\p{L}\p{N}]/u.test(text)) return { type: 'blank' };

  const heading = marked ? null : findHeading(text);
  if (heading) {
    const inner = findFormat(heading.after);
    const restLeft = inner.rest.trim();
    const exercise = restLeft && !isEmptyRest(restLeft) ? exerciseFrom(restLeft, true, exercises) ?? undefined : undefined;
    if (/^(?:superset|tri[-\s]?set)/i.test(text)) inner.hints.superset = 'heading';
    return { type: 'heading', kind: heading.kind, hints: inner.hints, ...(exercise ? { exercise } : {}) };
  }

  const format = findFormat(text);
  if (hasHints(format.hints)) {
    const left = format.rest.trim();
    if (isEmptyRest(left)) return { type: 'format', hints: format.hints };
    const exercise = exerciseFrom(left, marked, exercises);
    // "AMRAP 10 min: 10 burpees" is an exercise in that format; "20 MIN KETTLEBELL EMOM" is a format with a title.
    if (exercise && (exercise.status === 'sure' || exercise.amount || marked)) return { type: 'exercise', exercise, hints: format.hints };
    // Prose around a format: "Complete 4 rounds, resting 60 seconds between rounds."
    const rest = findRest(left);
    if (rest) {
      const target = rest.target ?? (format.hints.rounds ? 'round' : format.hints.sets ? 'set' : 'exercise');
      if (target === 'round') format.hints.roundRestSec = rest.seconds;
      else if (target === 'set') format.hints.setRestSec = rest.seconds;
      else format.hints.restSec = rest.seconds;
    }
    return { type: 'format', hints: format.hints, ...(NOISE.test(text) ? {} : { title: titleFrom(text.replace(ROUNDS_PHRASE, ' ')) }) };
  }

  const rest = findRest(text);
  if (rest) {
    const left = text.replace(re(`${B}(?:rest(?:ing)?|pause|break|erholung|ruhe|recover|recovery)\\b`, 'iu'), ' ')
      .replace(/\d{1,2}:\d{2}/, ' ').replace(re(`${B}${N}\\s*(?:${MIN}|${SEC})`, 'iu'), ' ');
    if (isEmptyRest(left)) return { type: 'rest', ...rest };
  }

  const exercise = exerciseFrom(text, marked, exercises);
  if (exercise) return { type: 'exercise', exercise, hints: {}, ...(group ? { group } : {}) };
  return { type: 'noise', text: text.slice(0, 120), ...(NOISE.test(text) || /[?]$/.test(text) ? {} : { title: titleFrom(text) }) };
}

function titleFrom(text: string): string | undefined {
  const t = tidyName(text.replace(/[:]+$/, ''));
  const words = t.split(/\s+/).length;
  if (!/\p{L}{3}/u.test(t) || words > 9 || t.length > 70) return undefined;
  return t;
}

// Pass 2: build sections -------------------------------------------------------------------------------

interface Building { kind: SectionKind; hints: Hints; exercises: ImportExercise[]; explicit: boolean }

function mergeHints(into: Hints, from: Hints) {
  Object.assign(into, from);
}

/** Has a format of its own already (not just a rest time). */
function formatted(h: Hints): boolean {
  return !!(h.kind || h.rounds || h.workSec || h.sets);
}

function finishSection(b: Building): ImportSection {
  const h = b.hints;
  const n = b.exercises.length;
  const assumed: string[] = [];
  const allHaveAmounts = b.exercises.every((e) => e.amount);
  let format: SectionFormat;
  if (h.kind === 'emom') {
    let minutes = h.minutes;
    if (!minutes) {
      minutes = n * (h.rounds ?? 3);
      assumed.push(`${minutes} minutes assumed (${h.rounds ? `${h.rounds} rounds` : '3 rounds'} of ${n} ${n === 1 ? 'exercise' : 'exercises'})`);
    }
    format = { kind: 'emom', minutes: clamp(minutes, LIMIT.minutes) };
  } else if (h.kind === 'amrap' || h.kind === 'continuous') {
    let minutes = h.minutes;
    if (!minutes) {
      minutes = h.kind === 'amrap' ? 10 : 20;
      assumed.push(`${minutes} minutes assumed`);
    }
    format = { kind: h.kind, minutes: clamp(minutes, LIMIT.minutes) };
  } else if (h.kind === 'tabata') {
    format = { kind: 'circuit', rounds: clamp(h.rounds ?? 8, LIMIT.rounds), workSec: h.workSec ?? 20, restSec: h.restSec ?? 10, ...(h.roundRestSec ? { roundRestSec: clamp(h.roundRestSec, LIMIT.roundRestSec) } : {}) };
  } else if (h.superset && n >= 2 && !h.workSec) {
    // Supersets alternate: one round is the pair (or group) once, the sets become rounds.
    const sets = h.rounds ?? h.sets ?? b.exercises.find((e) => e.sets !== undefined)?.sets;
    if (!sets) assumed.push('3 rounds assumed');
    const after = h.setRestSec ?? h.roundRestSec;
    if (after === undefined) assumed.push('90 s rest after each round assumed');
    format = { kind: 'circuit', rounds: clamp(sets ?? 3, LIMIT.rounds), workSec: 40, restSec: clamp(h.restSec ?? 0, LIMIT.restSec), roundRestSec: clamp(after ?? 90, LIMIT.roundRestSec) };
  } else if (h.workSec || h.rounds) {
    if (!h.rounds) assumed.push('3 rounds assumed');
    if (!h.workSec && !allHaveAmounts) assumed.push('40 s per exercise assumed where no amount is given');
    format = {
      kind: 'circuit',
      rounds: clamp(h.rounds ?? 3, LIMIT.rounds),
      workSec: clamp(h.workSec ?? 40, LIMIT.workSec),
      restSec: clamp(h.restSec ?? 0, LIMIT.restSec),
      ...(h.roundRestSec ? { roundRestSec: clamp(h.roundRestSec, LIMIT.roundRestSec) } : {}),
    };
  } else if (h.sets) {
    if (h.setRestSec === undefined) assumed.push('90 s rest between sets assumed');
    format = { kind: 'sets', sets: clamp(h.sets, LIMIT.sets), restSec: clamp(h.setRestSec ?? 90, LIMIT.roundRestSec) };
  } else if (h.restSec) {
    // A plain list with "Rest 60 sec between exercises": one round with that rest after each exercise.
    format = { kind: 'circuit', rounds: 1, workSec: 40, restSec: clamp(h.restSec, LIMIT.restSec) };
    if (!allHaveAmounts) assumed.push('40 s per exercise assumed where no amount is given');
  } else {
    format = { kind: 'list' };
  }
  const supersetRounds = h.superset && format.kind === 'circuit';
  // Sets inside another format ("3x10" in a circuit) stay visible as a note; in a superset they became the rounds.
  const exercises = b.exercises.map((e) => {
    if (e.sets === undefined || format.kind === 'sets' || supersetRounds) return e;
    const setsNote = `${e.sets} ${e.sets === 1 ? 'set' : 'sets'}`;
    return { ...e, note: e.note ? `${setsNote}, ${e.note}` : setsNote };
  });
  return { kind: b.kind, format, assumed, exercises };
}

/** Parses pasted workout text into a draft. Offline and synchronous; about 1 ms per line. */
export function parseWorkoutText(text: string, options: ParseOptions = {}): ImportDraft {
  const exercises = options.exercises ?? EXERCISES;
  const input = text.slice(0, IMPORT_MAX_CHARS);
  const sourceHint = MENTION.exec(input)?.[2];
  MENTION.lastIndex = 0;
  const lines = input.split(/\r?\n|\r|\s+(?=[\u2022\u25AA\u25B8\u25BA\u25CF]\s)/u).slice(0, 400);
  const items = lines.map((l) => classify(l, exercises));

  const sections: Building[] = [];
  let cur: Building | null = null;
  let pending: Hints = {};
  let title: string | undefined;
  const ignored: string[] = [];
  let ignoredLines = 0;
  let seenContent = false;
  /** A title only comes from lines before the first format or exercise line. */
  let seenFormat = false;
  /** "Rest 90 s between sets" once for the whole list applies to every sets part. */
  let setRest: number | undefined;

  const start = (kind: SectionKind, explicit: boolean): Building => {
    const b: Building = { kind, hints: {}, exercises: [], explicit };
    if (kind === 'main' && hasHints(pending)) {
      b.hints = pending;
      pending = {};
    }
    sections.push(b);
    cur = b;
    return b;
  };
  /** The next item that matters, skipping blank lines, noise and rest lines. */
  const nextIsExercise = (i: number): boolean => {
    for (let j = i + 1; j < items.length; j++) {
      const t = items[j]!.type;
      if (t === 'blank' || t === 'noise' || t === 'rest') continue;
      return t === 'exercise';
    }
    return false;
  };

  items.forEach((item, i) => {
    switch (item.type) {
      case 'blank':
        return;
      case 'noise':
        // The title: the first short line before the workout, or a later one that names a workout ("Leg day").
        if (!seenContent && !seenFormat && item.title && (!title || (!TITLE_WORDS.test(title) && TITLE_WORDS.test(item.title)))) title = item.title;
        else {
          ignoredLines++;
          if (ignored.length < 100) ignored.push(item.text);
        }
        return;
      case 'heading': {
        seenContent = true;
        const c = cur as Building | null;
        // An empty section nobody named yet takes the heading instead of leaving an empty one behind.
        const b = c && !c.exercises.length && !c.explicit ? ((c.kind = item.kind), (c.explicit = true), c) : start(item.kind, true);
        if (b.kind === 'main' && hasHints(pending)) { mergeHints(b.hints, pending); pending = {}; }
        mergeHints(b.hints, item.hints);
        if (item.exercise) b.exercises.push(item.exercise);
        return;
      }
      case 'format': {
        if (!seenContent && !seenFormat && !title && item.title) title = item.title;
        seenFormat = true;
        const c = cur as Building | null;
        if (!c) {
          // Before any section: the format belongs to the first main part ("20 MIN EMOM" as the first line).
          mergeHints(pending, item.hints);
          return;
        }
        if (!c.exercises.length) {
          mergeHints(c.hints, item.hints);
          return;
        }
        const more = nextIsExercise(i);
        // After a list of exercises: "Repeat 3x" or a format with nothing after it closes that list;
        // a format followed by more exercises starts the next main part.
        if (!more || (item.hints.repeat && !formatted(c.hints))) {
          mergeHints(c.hints, item.hints);
          return;
        }
        const b = start('main', false);
        mergeHints(b.hints, item.hints);
        return;
      }
      case 'rest': {
        const c = (cur as Building | null) ?? start('main', false);
        const isSets = !!c.hints.sets || c.exercises.some((e) => e.sets !== undefined);
        const target = item.target ?? (isSets ? 'set' : c.exercises.length && !nextIsExercise(i) ? 'round' : 'exercise');
        if (target === 'set') {
          c.hints.setRestSec = item.seconds;
          if (item.target === 'set' || !c.exercises.length || !nextIsExercise(i)) setRest = item.seconds;
        }
        else if (target === 'round') c.hints.roundRestSec = item.seconds;
        else c.hints.restSec = item.seconds;
        return;
      }
      case 'exercise': {
        seenContent = true;
        let c = (cur as Building | null) ?? start('main', false);
        if (hasHints(item.hints)) {
          if (c.exercises.length && formatted(c.hints) && formatted(item.hints)) c = start('main', false);
          mergeHints(c.hints, item.hints);
        }
        const ex = item.exercise;
        // Supersets "A1. / A2.": same letter, same section; a new letter or a line without one starts the next.
        if (item.group) {
          if (c.hints.superset !== item.group && !(c.hints.superset === 'heading' && !c.exercises.length)) {
            if (c.exercises.length || formatted(c.hints)) c = start('main', false);
          }
          c.hints.superset = item.group;
          if (ex.sets !== undefined && c.hints.rounds === undefined && c.hints.sets === undefined) c.hints.sets = ex.sets;
          c.exercises.push(ex);
          return;
        }
        if (c.hints.superset && c.hints.superset !== 'heading') c = start('main', false);
        if (c.hints.superset === 'heading') {
          if (ex.sets !== undefined && c.hints.rounds === undefined && c.hints.sets === undefined) c.hints.sets = ex.sets;
          c.exercises.push(ex);
          return;
        }
        // Gym lists: each sets-and-reps scheme becomes its own main part ("5x5 squat", then "3x10 rows").
        if (ex.sets !== undefined && c.kind === 'main' && !c.hints.kind && !c.hints.rounds && !c.hints.workSec) {
          if (c.hints.sets === undefined && !c.exercises.length) c.hints.sets = ex.sets;
          else if (c.hints.sets !== ex.sets) {
            c = start('main', false);
            c.hints.sets = ex.sets;
          }
        }
        c.exercises.push(ex);
        return;
      }
    }
  });
  if (setRest !== undefined) for (const s of sections) if (s.hints.setRestSec === undefined) s.hints.setRestSec = setRest;
  if (hasHints(pending)) {
    const firstMain = sections.find((s) => s.kind === 'main' && !formatted(s.hints));
    if (firstMain) mergeHints(firstMain.hints, pending);
  }

  return {
    ...(title ? { title } : {}),
    ...(sourceHint ? { sourceHint: `@${sourceHint}` } : {}),
    sections: sections.filter((s) => s.exercises.length).map(finishSection),
    ignoredLines,
    ignored,
  };
}

/** The names in a draft the offline matcher was not sure about (each once), for the AI name matching help. */
export function unsureNames(draft: ImportDraft): string[] {
  return [...new Set(draft.sections.flatMap((s) => s.exercises.filter((e) => e.status !== 'sure' && e.by !== 'ai').map((e) => e.name)))];
}

/**
 * Puts the AI's answers into a draft: a line the matcher was not sure about gets the AI's exercise preselected,
 * still as 'check' (the person confirms). An answer naming an exercise outside the library is ignored.
 */
export function applyAiMatches(draft: ImportDraft, answers: Array<{ name: string; exerciseId: string | null }>, exercises: Exercise[]): ImportDraft {
  const byId = new Map(exercises.map((e) => [e.id, e]));
  const picked = new Map<string, Exercise>();
  for (const a of answers) {
    const e = a.exerciseId ? byId.get(a.exerciseId) : undefined;
    if (e) picked.set(a.name, e);
  }
  if (!picked.size) return draft;
  return {
    ...draft,
    sections: draft.sections.map((s) => ({
      ...s,
      exercises: s.exercises.map((e) => {
        const p = e.status !== 'sure' ? picked.get(e.name) : undefined;
        if (!p) return e;
        const candidates = e.candidates.some((c) => c.exerciseId === p.id) ? e.candidates : [{ exerciseId: p.id, name: p.name, score: 0 }, ...e.candidates].slice(0, 5);
        return { ...e, status: 'check' as const, exerciseId: p.id, by: 'ai' as const, candidates };
      }),
    })),
  };
}

/**
 * parseWorkoutText, then the AI fallback for the lines the matcher was not sure about. An answer outside the
 * library is ignored. Without a fallback this is the plain parser.
 */
export async function parseWorkoutTextWithFallback(text: string, options: ParseOptions & { aiFallback?: ImportAiFallback } = {}): Promise<ImportDraft> {
  const draft = parseWorkoutText(text, options);
  if (!options.aiFallback) return draft;
  const unsure = unsureNames(draft);
  if (!unsure.length) return draft;
  return applyAiMatches(draft, await options.aiFallback(unsure), options.exercises ?? EXERCISES);
}
